import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  CreditCardIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { walletApi } from '@/api/walletApi'
import { Invoice, InvoiceStatus } from '@/api/invoiceApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const statusColors: Record<string, { bg: string; text: string; icon: any }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', icon: DocumentTextIcon },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon },
  SENT: { bg: 'bg-blue-100', text: 'text-blue-800', icon: DocumentTextIcon },
  PARTIALLY_PAID: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon },
  PAID: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
  OVERDUE: { bg: 'bg-red-100', text: 'text-red-800', icon: ExclamationCircleIcon },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-500', icon: XMarkIcon },
  REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-800', icon: XMarkIcon },
}

// Type colors (Invoice model uses 'type' instead of 'category')
const typeColors: Record<string, string> = {
  SCHOOL_FEES: 'bg-blue-100 text-blue-800',
  TRANSPORT: 'bg-purple-100 text-purple-800',
  LIBRARY: 'bg-indigo-100 text-indigo-800',
  EXAM_FEES: 'bg-pink-100 text-pink-800',
  UNIFORM: 'bg-orange-100 text-orange-800',
  BOOKS: 'bg-teal-100 text-teal-800',
  MEALS: 'bg-yellow-100 text-yellow-800',
  ACTIVITIES: 'bg-green-100 text-green-800',
  OTHER: 'bg-gray-100 text-gray-800',
}

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function isOverdue(dueDate: string) {
  return new Date(dueDate) < new Date()
}

export default function ParentInvoicesPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('')
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [showPayModal, setShowPayModal] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState<number>(0)

  // Get wallet balance
  const { data: walletData } = useQuery({
    queryKey: ['myWallet'],
    queryFn: () => walletApi.getMyWallet(),
  })

  // Get invoices
  const { data, isLoading } = useQuery({
    queryKey: ['myInvoices', page, statusFilter],
    queryFn: () =>
      walletApi.getInvoices({
        page,
        limit: 10,
        status: statusFilter || undefined,
      }),
  })

  const payMutation = useMutation({
    mutationFn: ({ invoiceId, amount }: { invoiceId: string; amount?: number }) =>
      walletApi.payInvoice({ invoiceId, amount }),
    onSuccess: () => {
      toast.success('Payment successful!')
      queryClient.invalidateQueries({ queryKey: ['myInvoices'] })
      queryClient.invalidateQueries({ queryKey: ['myWallet'] })
      queryClient.invalidateQueries({ queryKey: ['myTransactions'] })
      setShowPayModal(false)
      setSelectedInvoice(null)
      setPaymentAmount(0)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Payment failed')
    },
  })

  const handlePayClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice)
    setPaymentAmount(invoice.balanceDue)
    setShowPayModal(true)
  }

  const handlePaySubmit = () => {
    if (!selectedInvoice) return
    payMutation.mutate({
      invoiceId: selectedInvoice._id,
      amount: paymentAmount,
    })
  }

  const invoices = data?.data?.invoices || []
  const pagination = data?.data?.pagination || { page: 1, limit: 10, total: 0, pages: 0 }
  const walletBalance = walletData?.data?.balance || 0

  // Calculate totals
  const pendingInvoices = invoices.filter(
    (inv: Invoice) => inv.status !== 'PAID' && inv.status !== 'CANCELLED'
  )
  const totalDue = pendingInvoices.reduce((sum: number, inv: Invoice) => sum + inv.balanceDue, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fee Invoices</h1>
          <p className="text-gray-600">View and pay your fee invoices</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Wallet Balance</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(walletBalance)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CreditCardIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Due</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDue)}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Invoices</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingInvoices.length}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">Filter by status:</span>
          <div className="flex gap-2">
            {['', 'SENT', 'PARTIALLY_PAID', 'OVERDUE', 'PAID'].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setStatusFilter(status as InvoiceStatus | '')
                  setPage(1)
                }}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  statusFilter === status
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === '' ? 'All' : status.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No invoices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice: Invoice) => {
                  const StatusIcon = statusColors[invoice.status]?.icon || DocumentTextIcon
                  const invoiceIsOverdue = isOverdue(invoice.dueDate) && invoice.status !== 'PAID' && invoice.status !== 'CANCELLED'

                  return (
                    <tr key={invoice._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{invoice.title}</p>
                          <p className="text-xs text-gray-500">{invoice.invoiceNumber}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${typeColors[invoice.type] || 'bg-gray-100 text-gray-800'}`}>
                          {invoice.type?.replace('_', ' ') || 'Other'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <ClockIcon className="w-4 h-4" />
                          {formatDate(invoice.dueDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full w-fit ${
                              statusColors[invoice.status]?.bg
                            } ${statusColors[invoice.status]?.text}`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {invoice.status.replace('_', ' ')}
                          </span>
                          {invoiceIsOverdue && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 w-fit">
                              <ExclamationCircleIcon className="w-3 h-3" />
                              Overdue
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <p className="text-sm font-medium text-gray-900">{formatCurrency(invoice.grandTotal)}</p>
                        {invoice.paidAmount > 0 && (
                          <p className="text-xs text-green-600">Paid: {formatCurrency(invoice.paidAmount)}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {invoice.balanceDue > 0 && invoice.status !== 'CANCELLED' ? (
                          <p className="text-sm font-semibold text-red-600">{formatCurrency(invoice.balanceDue)}</p>
                        ) : (
                          <p className="text-sm text-gray-500">-</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/parent/wallet/invoices/${invoice._id}`)}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          {invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && (
                            <button
                              onClick={() => handlePayClick(invoice)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
                            >
                              <CreditCardIcon className="w-4 h-4" />
                              Pay
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-4">
          <p className="text-sm text-gray-600">
            Showing {(page - 1) * pagination.limit + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === pagination.pages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {showPayModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Pay Invoice</h3>
              <p className="text-sm text-gray-500">{selectedInvoice.invoiceNumber}</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Invoice Amount</span>
                  <span className="font-medium">{formatCurrency(selectedInvoice.grandTotal)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Already Paid</span>
                  <span className="text-green-600">{formatCurrency(selectedInvoice.paidAmount)}</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-2">
                  <span className="font-medium">Balance Due</span>
                  <span className="font-bold text-red-600">{formatCurrency(selectedInvoice.balanceDue)}</span>
                </div>
              </div>

              {/* Quick Payment Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Payment Amount
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentAmount(selectedInvoice.balanceDue)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      paymentAmount === selectedInvoice.balanceDue
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Full Amount
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentAmount(Math.round(selectedInvoice.balanceDue * 0.5))}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      paymentAmount === Math.round(selectedInvoice.balanceDue * 0.5)
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    50%
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentAmount(Math.round(selectedInvoice.balanceDue * 0.25))}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      paymentAmount === Math.round(selectedInvoice.balanceDue * 0.25)
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    25%
                  </button>
                </div>
              </div>

              {/* Custom Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Or enter custom amount
                </label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(Number(e.target.value))}
                  max={selectedInvoice.balanceDue}
                  min={1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter amount"
                />
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    Wallet Balance: {formatCurrency(walletBalance)}
                  </span>
                  {paymentAmount < selectedInvoice.balanceDue && paymentAmount > 0 && (
                    <span className="text-blue-600">
                      Partial payment
                    </span>
                  )}
                </div>
                {paymentAmount > walletBalance && (
                  <p className="mt-1 text-sm text-red-600">
                    Insufficient wallet balance
                  </p>
                )}
                {paymentAmount > selectedInvoice.balanceDue && (
                  <p className="mt-1 text-sm text-red-600">
                    Amount exceeds balance due
                  </p>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPayModal(false)
                  setSelectedInvoice(null)
                  setPaymentAmount(0)
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handlePaySubmit}
                disabled={payMutation.isPending || paymentAmount > walletBalance || paymentAmount <= 0 || paymentAmount > selectedInvoice.balanceDue}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {payMutation.isPending ? 'Processing...' : `Pay ${formatCurrency(paymentAmount)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
